import React, { useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import {
  todayStr, addDays, addMonths, startOfWeek, startOfMonth,
  monthLabel, civilToLocalDate, fmtCivil, isPast,
} from '../dates';
import { WP_TOKENS, WP_CAL_CSS } from '../workingPapers';

// The planner's Phōkii (CALENDAR-DESIGN.md phase 2), wearing the Working
// Papers facelift: THE BLOTTER — a desk pad with the month printed on it,
// held at four leather corners, sheets stacked visibly beneath, standing on
// the same green leather desk as the day book and under the same lamp
// (above-left) as the Marked Pages shelf.
//
// The behaviour is untouched. One lens at a time: green edge = do (yours to
// move), warm red edge = due (the world's). D flips the lens. Clicking the
// active mode returns to now. Click an entry → the note.

const DOW = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export default function CalendarView({ actions, projects, getProjectColorById, onOpenAction }) {
  const [mode, setMode] = useState(() => localStorage.getItem('phokii-cal-mode') || 'month');
  // One lens at a time — Do (your plan) or Due (the world's deadlines).
  // Sticky; D toggles. Prevents an action with both dates chipping twice.
  const [filter, setFilter] = useState(() => localStorage.getItem('phokii-cal-filter') || 'due');
  const [anchor, setAnchor] = useState(todayStr());
  const [pulse, setPulse] = useState(false);
  const today = todayStr();

  const setModePersist = (m) => { setMode(m); localStorage.setItem('phokii-cal-mode', m); };
  const setFilterPersist = (f) => { setFilter(f); localStorage.setItem('phokii-cal-filter', f); };

  // D flips the lens while the calendar is up (ignores typing contexts)
  React.useEffect(() => {
    const onKey = (e) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.isContentEditable) return;
      if (e.key === 'd' || e.key === 'D') {
        e.preventDefault();
        setFilterPersist(filter === 'do' ? 'due' : 'do');
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [filter]);

  // date → entries for the ACTIVE lens only.
  // Do lens: planned entries on their doDate — and floating ✦ focus items
  // materialize on today (that's what floating means). Deduped when both.
  // Due lens: deadline entries on their dueDate, with covered/overdue states.
  const byDate = useMemo(() => {
    const map = {};
    const add = (date, entry) => { (map[date] = map[date] || []).push(entry); };
    for (const g of actions) {
      if (g.completed) continue;
      if (filter === 'do') {
        if (g.doDate) add(g.doDate, { kind: 'do', g });
        // The star fills a gap, it does not override a plan. A starred action
        // with a day of its own stays on that day (still wearing its ✦);
        // only undated stars float to today. Otherwise one action appeared
        // twice — once where you planned it, once because it mattered.
        if (g.pho && !g.doDate) add(today, { kind: 'focus', g });
      } else {
        if (g.dueDate) add(g.dueDate, { kind: 'due', g });
      }
    }
    for (const date of Object.keys(map)) {
      map[date].sort((a, b) => (a.kind === b.kind ? 0 : a.kind === 'focus' ? -1 : 1));
    }
    return map;
  }, [actions, filter, today]);

  const days = useMemo(() => {
    if (mode === 'day') return [anchor];
    if (mode === 'week') {
      const start = startOfWeek(anchor);
      return Array.from({ length: 7 }, (_, i) => addDays(start, i));
    }
    const first = startOfMonth(anchor);
    const gridStart = startOfWeek(first);
    // 6 weeks always — stable grid height
    return Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));
  }, [mode, anchor]);

  const nav = (dir) => {
    setAnchor(
      mode === 'day' ? addDays(anchor, dir)
      : mode === 'week' ? addDays(anchor, dir * 7)
      : addMonths(anchor, dir)
    );
  };

  const inMonth = (d) => d.slice(0, 7) === anchor.slice(0, 7);

  // An entry is a written line, not a widget. A month cell is narrow, so it
  // prints only what the red mark cannot already say — the exceptions. Week
  // and day have the room to print all of it.
  const Entry = ({ entry }) => {
    const { kind, g } = entry;
    const overdue = kind === 'due' && isPast(g.dueDate);
    const covered = kind === 'due' && g.doDate && g.doDate <= g.dueDate && !isPast(g.doDate);
    const projColor = g.linkType === 'project' && g.linkId ? getProjectColorById(g.linkId) : null;
    const month = mode === 'month';
    const tip = kind === 'due'
      ? `${g.text} — due ${fmtCivil(g.dueDate)}${g.doDate ? ` · you planned ${fmtCivil(g.doDate, { month: 'short', day: 'numeric' })}` : ''}`
      : kind === 'focus'
        ? `${g.text} — starred, so it sits on today${g.dueDate ? ` · due ${fmtCivil(g.dueDate, { month: 'short', day: 'numeric' })}` : ''}`
        : `${g.text} — your plan${g.dueDate ? ` · due ${fmtCivil(g.dueDate, { month: 'short', day: 'numeric' })}` : ''}`;

    let tag = null;
    if (kind === 'due') {
      tag = overdue ? <span className="tag due">overdue</span>
        : covered ? <span className="tag ok">planned</span>
        : month ? null
        : <span className="tag due">due</span>;
    } else if (g.dueDate) {
      tag = isPast(g.dueDate) ? <span className="tag due">overdue</span>
        : month ? null
        : <span className="tag plain">due {fmtCivil(g.dueDate, { month: 'short', day: 'numeric' })}</span>;
    }

    return (
      <button
        type="button"
        onClick={() => onOpenAction(g.id)}
        className={`wp-ent k-${kind}${overdue ? ' od' : ''}`}
        title={`${tip}${g.nextStep ? `\n→ ${g.nextStep}` : ''}`}
      >
        {projColor && <span className="pd" style={{ background: projColor }} />}
        <span className="t">
          {g.pho && <i>✦</i>}
          {g.text}
          {mode === 'day' && g.nextStep && <span className="ns">→ {g.nextStep}</span>}
        </span>
        {tag}
      </button>
    );
  };

  const totalEntries = Object.values(byDate).reduce((n, arr) => n + arr.length, 0);

  const title = mode === 'day'
    ? fmtCivil(anchor, { weekday: 'long', month: 'long', day: 'numeric' })
    : mode === 'week'
      ? `${fmtCivil(startOfWeek(anchor), { month: 'short', day: 'numeric' })} – ${fmtCivil(addDays(startOfWeek(anchor), 6), { month: 'short', day: 'numeric' })}`
      : monthLabel(anchor);

  return (
    <div className="flex-1 flex flex-col overflow-y-auto themed-background" style={{ minHeight: 'calc(100vh - 56px)' }}>
      <style>{WP_TOKENS + WP_CAL_CSS}</style>
      <div className="p-4">
        <div className="wp-desk wp-cal">
          <div className="wp-padwrap">
            {/* held down at four leather corners */}
            <span className="wp-cnr tl" aria-hidden="true"><i /></span>
            <span className="wp-cnr tr" aria-hidden="true"><i /></span>
            <span className="wp-cnr bl" aria-hidden="true"><i /></span>
            <span className="wp-cnr br" aria-hidden="true"><i /></span>

            <div className="wp-pad">
              <div className="wp-cmast">
                <h2 className="wp-caltitle">{title}</h2>
                <div className="wp-arrows">
                  <button type="button" className="wp-arrow" onClick={() => nav(-1)} title="Previous" aria-label="Previous"><ChevronLeft size={13} /></button>
                  <button type="button" className="wp-arrow" onClick={() => nav(1)} title="Next" aria-label="Next"><ChevronRight size={13} /></button>
                </div>

                {/* Three views. Clicking the active one again returns to now. */}
                <div className="wp-itabs" role="group" aria-label="Day, week or month">
                  {['day', 'week', 'month'].map(m => (
                    <button
                      key={m}
                      type="button"
                      className="wp-itab"
                      aria-pressed={mode === m}
                      onClick={() => {
                        if (mode === m) { setAnchor(todayStr()); setPulse(true); setTimeout(() => setPulse(false), 900); }
                        else setModePersist(m);
                      }}
                      title={mode === m ? `Back to this ${m}` : `${m[0].toUpperCase()}${m.slice(1)} view`}
                    >{m}</button>
                  ))}
                </div>

                {/* The lens: one at a time, sticky, D flips it */}
                <div className="wp-lens" role="group" aria-label="Do or Due — press D to flip" title="Toggle with D">
                  {[
                    { key: 'do', label: 'Do', color: 'var(--wp-do)' },
                    { key: 'due', label: 'Due', color: 'var(--wp-due)' },
                  ].map(f => (
                    <button
                      key={f.key}
                      type="button"
                      aria-pressed={filter === f.key}
                      onClick={() => setFilterPersist(f.key)}
                    >
                      <i style={{ background: f.color }} />
                      {f.label}
                    </button>
                  ))}
                </div>

                <div className="wp-calgap" />
                <div className="wp-say">
                  {filter === 'do'
                    ? 'your plan — a ✦ star sits on today · D for the days the world set'
                    : "the days the world set · D for your plan"}
                </div>
              </div>

              {totalEntries === 0 && (
                <div className="wp-calblank">
                  <div className="g">Nothing on the blotter in this lens.</div>
                  <div className="s">
                    {filter === 'do'
                      ? <>Star ✦ an action and it sits on today, or open one and write a <b style={{ color: 'var(--wp-do)' }}>Do</b> day. Press D for the days the world set.</>
                      : <>Open an action and write the <b style={{ color: 'var(--wp-due)' }}>Due</b> day the world needs it by. Press D for your own plan.</>}
                  </div>
                </div>
              )}

              {totalEntries > 0 && (
                <>
                  {mode !== 'day' && (
                    <div className="wp-dow">
                      {DOW.map(d => <span key={d}>{d}</span>)}
                    </div>
                  )}
                  <div className={`wp-grid ${mode}`}>
                    {days.map(d => {
                      const isToday = d === today;
                      const dim = mode === 'month' && !inMonth(d);
                      const dow = civilToLocalDate(d).getDay();
                      const weekend = dow === 0 || dow === 6;
                      return (
                        <div
                          key={d}
                          className={`wp-cell${weekend ? ' we' : ''}${dim ? ' out' : ''}${isToday ? ' now' : ''}${isToday && pulse ? ' pulse' : ''}`}
                        >
                          <div className="num">
                            <b>{Number(d.slice(8, 10))}</b>
                            {mode !== 'month' && <span className="mon">{fmtCivil(d, { month: 'short' })}</span>}
                            {isToday && <span className="tdy">today</span>}
                          </div>
                          <div className="stack">
                            {(byDate[d] || []).map((entry, i) => <Entry key={`${entry.g.id}-${entry.kind}-${i}`} entry={entry} />)}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
